package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.core.uuidV7
import com.dotdrives.model.*
import com.dotdrives.repository.DonorRepository
import kotlin.time.Clock
import kotlinx.coroutines.*

data class DonorUiState(
    val loading: Boolean = true,
    val organizations: List<DonorOrganization> = emptyList(),
    val orgId: String? = null,
    val snapshot: DonorSnapshot? = null,
    val updates: List<DonorUpdate> = emptyList(),
    val openUpdateId: String? = null,
    val moreBusy: Boolean = false,
    val actionBusy: Boolean = false,
    val error: String? = null,
    val message: String? = null,
    val documentId: String? = null,
    val document: DocumentDownload? = null,
    val documentBusy: Boolean = false,
    val pendingOperation: DonorRecurringRequest? = null,
    val redirectUrl: String? = null,
)

class DonorController(
    private val repo: DonorRepository,
    scope: CoroutineScope,
    private val operationStore: DonorOperationStore = MemoryDonorOperationStore(),
) : DotController<DonorUiState>(DonorUiState(), scope) {
    private var generation = 0
    private var documentJob: Job? = null
    private var documentGeneration = 0

    fun start() {
        val ticket = ++generation
        documentJob?.cancel()
        documentGeneration++
        update { DonorUiState() }
        scope.launch {
            try {
                val orgs = repo.organizations().getOrThrow()
                if (ticket != generation) return@launch
                update { it.copy(loading = false, organizations = orgs) }
                orgs.singleOrNull()?.let { selectOrg(it.id) }
            } catch (e: Throwable) {
                fail(e, ticket)
            }
        }
    }

    fun selectOrg(id: String?) {
        if (id != null && state.value.organizations.none { it.id == id }) return
        val ticket = ++generation
        documentJob?.cancel()
        documentGeneration++
        update { DonorUiState(loading = id != null, organizations = it.organizations, orgId = id) }
        if (id == null) return
        scope.launch {
            try {
                val saved = operationStore.read(id)
                val snapshot = repo.page(id, null).getOrThrow()
                val updates = repo.updates(id).getOrThrow()
                if (ticket != generation) return@launch
                update {
                    it.copy(
                        loading = false,
                        snapshot = snapshot,
                        updates = updates,
                        pendingOperation = saved,
                    )
                }
            } catch (e: Throwable) {
                fail(e, ticket)
            }
        }
    }

    fun refresh() {
        state.value.orgId?.let(::selectOrg) ?: start()
    }

    fun loadMore() {
        val s = state.value
        val org = s.orgId ?: return
        val cursor = s.snapshot?.nextAfter ?: return
        if (s.moreBusy) return
        val ticket = generation
        update { it.copy(moreBusy = true, error = null) }
        scope.launch {
            try {
                val next = repo.page(org, cursor).getOrThrow()
                if (ticket != generation) return@launch
                check(next.giverId == state.value.snapshot?.giverId) {
                    "Your giving identity changed. Refresh the portal."
                }
                update {
                    it.copy(
                        moreBusy = false,
                        snapshot =
                            it.snapshot?.copy(
                                donations =
                                    (it.snapshot.donations + next.donations).distinctBy { gift ->
                                        gift.id
                                    },
                                nextAfter = next.nextAfter,
                            ),
                    )
                }
            } catch (e: Throwable) {
                fail(e, ticket)
            }
        }
    }

    fun openUpdate(id: String) {
        val org = state.value.orgId ?: return
        if (state.value.updates.none { it.id == id }) return
        action { ticket ->
            repo.openUpdate(org, id).getOrThrow()
            current(ticket)
            update {
                it.copy(
                    openUpdateId = id,
                    updates =
                        it.updates.map { row ->
                            if (row.id == id)
                                row.copy(openedAt = row.openedAt ?: Clock.System.now())
                            else row
                        },
                )
            }
        }
    }

    fun closeUpdate() {
        update { it.copy(openUpdateId = null) }
    }

    fun saveContact(phone: String, address: String) {
        val org = state.value.orgId ?: return
        if (phone.length > 80 || address.length > 1000) {
            update {
                it.copy(error = "Phone must be at most 80 characters and address at most 1,000.")
            }
            return
        }
        action { ticket ->
            repo.saveContact(org, phone, address).getOrThrow()
            current(ticket)
            update {
                it.copy(
                    snapshot =
                        it.snapshot?.copy(
                            profile = it.snapshot.profile.copy(phone = phone, address = address)
                        ),
                    message = "Contact details saved.",
                )
            }
        }
    }

    fun attachment(id: String) {
        val org = state.value.orgId ?: return
        // An action rejected by the shared busy gate must not change document selection.
        if (state.value.actionBusy) return
        documentJob?.cancel()
        val documentTicket = ++documentGeneration
        update { it.copy(documentId = id, document = null, documentBusy = false) }
        action { ticket ->
            try {
                val link = repo.attachment(org, id).getOrThrow()
                current(ticket)
                if (documentTicket == documentGeneration) {
                    update { it.copy(document = link) }
                }
            } catch (e: Throwable) {
                // A newer statement or attachment owns both the result and its error state.
                if (documentTicket == documentGeneration) throw e
            }
        }
    }

    fun document(id: String, retry: Boolean = false) {
        if (state.value.snapshot?.statements?.none { it.id == id } != false) return
        documentJob?.cancel()
        documentGeneration++
        val ticket = generation
        update { it.copy(documentId = id, document = null, documentBusy = true, error = null) }
        val documentTicket = documentGeneration
        documentJob =
            scope.launch {
                try {
                    if (retry) repo.document(id, true).getOrThrow()
                    repeat(12) { index ->
                        val result = repo.document(id, false).getOrThrow()
                        if (ticket != generation || documentTicket != documentGeneration)
                            return@launch
                        update { it.copy(document = result) }
                        if (result.status !in setOf("queued", "processing")) return@launch
                        if (index < 11) delay(5000)
                    }
                } catch (e: Throwable) {
                    if (documentTicket == documentGeneration) fail(e, ticket)
                } finally {
                    if (ticket == generation && documentTicket == documentGeneration)
                        update { it.copy(documentBusy = false) }
                }
            }
    }

    fun manage(subscriptionId: String, action: String, amount: String? = null, day: Int? = null) {
        val s = state.value
        val org = s.orgId ?: return
        if (
            s.actionBusy ||
                s.pendingOperation != null ||
                s.snapshot?.subscriptions?.none { it.id == subscriptionId } != false
        )
            return
        if (action !in setOf("pause", "resume", "cancel", "portal", "amount", "billing_day")) return
        if (
            action == "amount" &&
                (amount == null ||
                    !amount.matches(Regex("[0-9]{1,7}(\\.[0-9]{1,2})?")) ||
                    amount.toDoubleOrNull()?.let { it in 0.5..1000000.0 } != true)
        ) {
            update {
                it.copy(
                    error = "Enter an amount from $0.50 to $1,000,000 with at most two decimals."
                )
            }
            return
        }
        if (action == "billing_day" && day !in 1..28) {
            update { it.copy(error = "Choose a billing day from 1 to 28.") }
            return
        }
        val request = DonorRecurringRequest(action, org, subscriptionId, uuidV7(), amount, day)
        try {
            operationStore.save(request)
        } catch (e: Throwable) {
            fail(e, generation)
            return
        }
        update { it.copy(pendingOperation = request) }
        retryRecurring()
    }

    fun retryRecurring() {
        val request = state.value.pendingOperation ?: return
        action { ticket ->
            val result = try { repo.recurring(request).getOrThrow() }
            catch (rejected: RecurringRequestRejected) {
                current(ticket)
                operationStore.clear(request.orgId)
                update { it.copy(pendingOperation = null, error = rejected.message) }
                return@action
            }
            check(
                result.url != null || result.accepted && result.status == "pending_confirmation"
            ) {
                "Management response is incomplete. Retry the same request."
            }
            current(ticket)
            operationStore.clear(request.orgId)
            update {
                it.copy(
                    pendingOperation = null,
                    redirectUrl = result.url,
                    message =
                        if (result.url == null)
                            "Request accepted; awaiting payment provider confirmation. Refresh to check the recorded status."
                        else "Your secure payment settings are ready.",
                )
            }
        }
    }

    fun clearRedirect() {
        update { it.copy(redirectUrl = null) }
    }

    private fun action(block: suspend (Int) -> Unit) {
        if (state.value.actionBusy) return
        val ticket = generation
        update { it.copy(actionBusy = true, error = null, message = null) }
        scope.launch {
            try {
                block(ticket)
            } catch (e: Throwable) {
                fail(e, ticket)
            } finally {
                if (ticket == generation) update { it.copy(actionBusy = false) }
            }
        }
    }

    private fun current(ticket: Int) {
        if (ticket != generation) throw CancellationException("Donor context changed")
    }

    private fun fail(e: Throwable, ticket: Int) {
        if (e is CancellationException) throw e
        if (ticket == generation)
            update {
                it.copy(
                    loading = false,
                    moreBusy = false,
                    error = e.message ?: "Unable to load your giving. Please retry.",
                )
            }
    }
}
